import {
  AdminLevelType,
  AdminLevelUserRole,
  AdminLevelUserStatus,
  DashboardTimePeriod,
  ReportCommentType,
  ReportForwardingType,
  ReportMainCategory,
  ReportPublicationType,
  ReportStatusType,
  UserNotificationInterval,
  UserRole,
  UserStatus,
} from '@/modules/api';
import { AdminLevelActive } from '@/types/AdminLevelActive';
import { AdminLevelActiveExtended } from '@/types/AdminLevelActiveExtended';
import { CreateReportStep } from '@/types/CreateReportStep';
import { ManagedByParent } from '@/types/ManagedByParent';
import { MapLayer } from '@/types/MapLayer';
import { MapStyle } from '@/types/MapStyle';

export const I18N_DE = {
  AdminLevelActive: {
    [AdminLevelActive.TRUE]: 'Aktiv',
    [AdminLevelActive.FALSE]: 'Inaktiv',
  },
  AdminLevelActiveExtended: {
    [AdminLevelActiveExtended.ACTIVE]: 'Aktiv',
    [AdminLevelActiveExtended.INACTIVE_NONE]: 'Inaktiv',
    [AdminLevelActiveExtended.INACTIVE_LINK]: 'Inaktiv (Link)',
    [AdminLevelActiveExtended.INACTIVE_OPEN311]: 'Inaktiv (Open311)',
  },
  AdminLevelActiveDescription: {
    [AdminLevelActive.TRUE]: 'Für diese Verwaltungsebene können neue Meldungen erstellt werden.',
    [AdminLevelActive.FALSE]: 'Für diese Verwaltungsebene können keine neue Meldungen erstellt werden.',
  },

  ManagedByParent: {
    [ManagedByParent.TRUE]: 'Übergeordnet',
    [ManagedByParent.FALSE]: 'Eigenständig',
  },

  ManagedByParentDescription: {
    [ManagedByParent.TRUE]: 'Von übergeordneter Verwaltungsebene verwaltet',
    [ManagedByParent.FALSE]: 'Eigenständig verwaltet',
  },

  AdminLevelType: {
    [AdminLevelType.STATE]: 'Bundesland',
    [AdminLevelType.GOVERNMENT_DISTRICT]: 'Regierungsbezirk',
    [AdminLevelType.COUNTY]: 'Landkreis',
    [AdminLevelType.CITY_COUNTY]: 'Stadtkreis',
    [AdminLevelType.MUNICIPALITY]: 'Gemeinde',
  },
  AdminLevelTypeAbbreviation: {
    [AdminLevelType.STATE]: 'Bl.',
    [AdminLevelType.GOVERNMENT_DISTRICT]: 'Reg.-Bez.',
    [AdminLevelType.COUNTY]: 'Lk.',
    [AdminLevelType.CITY_COUNTY]: 'Stdk.',
    [AdminLevelType.MUNICIPALITY]: 'Gde.',
  },

  CreateReportStep: {
    [CreateReportStep.LOCATION]: 'Ort',
    [CreateReportStep.DUPLICATES]: 'Vorhandene Meldungen',
    [CreateReportStep.CATEGORY]: 'Kategorie',
    [CreateReportStep.DESCRIPTION]: 'Beschreibung',
    [CreateReportStep.CONFIRM]: 'Bestätigung und Kontakt',
  },

  geolocationError: 'Ihr Standort konnte nicht ermittelt werden',

  MapLayer: {
    [MapLayer.BICYCLE_NETWORK]: 'Radwegenetz',
    [MapLayer.ROUTE_SIGNAGE]: 'Beschilderung Radnetz BW',
    [MapLayer.SERVICE_STATION]: 'Servicestationen',
    [MapLayer.ADMIN_LEVELS_ACTIVE]: 'Teilnehmenden Kommunen',
  },

  MapStyle: {
    [MapStyle.VECTOR]: 'basemap.de',
    [MapStyle.SATELLITE]: 'Luftbilder',
  },

  ReportCommentType: {
    [ReportCommentType.PUBLIC]: 'Öffentlich',
    [ReportCommentType.INTERNAL]: 'Intern',
    [ReportCommentType.DESCRIPTION]: 'Beschreibung',
  },
  ReportCommentTypeLabel: {
    [ReportCommentType.PUBLIC]: 'Öffentlicher Kommentar',
    [ReportCommentType.INTERNAL]: 'Interner Kommentar',
    [ReportCommentType.DESCRIPTION]: 'Beschreibung',
  },
  ReportCommentTypeDescription: {
    [ReportCommentType.PUBLIC]: 'Der Kommentar ist für Bürger*innen sichtbar, wenn auch die Meldung öffentlich ist.',
    [ReportCommentType.INTERNAL]:
      'Der Kommentar ist für Bürger*innen verborgen und nur intern für die Verwaltung sichtbar.',
    [ReportCommentType.DESCRIPTION]: 'Initiale Beschreibung welche welche von den Meldenden gegeben wurde.',
  },

  ReportMainCategory: {
    [ReportMainCategory.ROUTE_SIGNAGE]: 'Beschilderung Radnetz BW',
    [ReportMainCategory.SIGNAGE]: 'Beschilderung',
    [ReportMainCategory.CONSTRUCTION_DEFECTS]: 'Bauliche Mängel',
    [ReportMainCategory.TRAFFIC_ROUTING]: 'Verkehrsführung',
    [ReportMainCategory.HAZARDS_AND_SAFETY]: 'Gefahren und Sicherheit',
    [ReportMainCategory.MARKING_AND_LIGHTING]: 'Markierung und Beleuchtung',
    [ReportMainCategory.TRAFFIC_LIGHTS]: 'Ampeln',
    [ReportMainCategory.MAINTENANCE]: 'Pflege',
    [ReportMainCategory.PARKING_AND_SERVICE]: 'Abstellen und Service',
    [ReportMainCategory.LIKE]: 'Lob',
    [ReportMainCategory.REQUESTS_AND_SUGGESTIONS]: 'Wünsche und Anregungen',
    [ReportMainCategory.EXTERNAL]: 'Extern',
  },

  ReportPublicationType: {
    [ReportPublicationType.PUBLIC]: 'Öffentlich',
    [ReportPublicationType.REDACTED]: 'Basisdaten',
    [ReportPublicationType.HIDDEN]: 'Verborgen',
  },
  ReportPublicationTypeDescription: {
    [ReportPublicationType.PUBLIC]:
      'Die Meldung ist für Bürger*innen vollständig sichtbar, ausgenommen interne Kommentare und zuständige Personen.',
    [ReportPublicationType.REDACTED]:
      'Die Meldung ist für Bürger*innen sichtbar, der zeitliche Verlauf (Statusänderungen, Kommentare) sowie Bilder bleiben verborgen.',
    [ReportPublicationType.HIDDEN]:
      'Die Meldung ist für Bürger*innen verborgen und es werden keine Benachrichtigungen versendet.',
  },

  ReportForwardingType: {
    [ReportForwardingType.INTERNAL]: 'Intern',
    [ReportForwardingType.PUBLIC]: 'Öffentlich',
    [ReportForwardingType.REDACTED]: 'Basisdaten',
  },
  ReportForwardingTypeDescription: {
    [ReportForwardingType.INTERNAL]:
      'Die Meldung wird vollständig weitergeleitet, inklusive interner Kommentare und Zuständigkeiten.',
    [ReportForwardingType.PUBLIC]:
      'Die Meldung wird inklusive dem öffentlich sichtbaren zeitlichen Verlauf weitergeleitet.',
    [ReportForwardingType.REDACTED]:
      'Die Meldung wird nur mit Basisdaten weitergeleitet, exklusive zeitlichem Verlauf.',
  },

  ReportStatusType: {
    [ReportStatusType.NEW]: 'Neu',
    [ReportStatusType.WORK_IN_PROGRESS]: 'In Bearbeitung',
    [ReportStatusType.COMPLETED]: 'Abgeschlossen',
    [ReportStatusType.NOT_REALIZABLE]: 'Nicht umsetzbar',
    [ReportStatusType.EXTERNAL]: 'Extern',
    [ReportStatusType.ARCHIVED]: 'Archiviert',
  },
  DashboardReportStatusType: {
    [ReportStatusType.NEW]: 'Neue Meldungen',
    [ReportStatusType.WORK_IN_PROGRESS]: 'Meldungen in Bearbeitung',
    [ReportStatusType.COMPLETED]: 'Abgeschlossene Meldungen',
    [ReportStatusType.NOT_REALIZABLE]: 'Nicht umsetzbare Meldungen',
    [ReportStatusType.EXTERNAL]: 'Extern Meldungen',
    [ReportStatusType.ARCHIVED]: 'Archivierte Meldungen',
  },
  ReportStatusTypeDescription: {
    [ReportStatusType.NEW]: 'Der Status sollte nach der initialen Sichtung geändert werden.',
    [ReportStatusType.WORK_IN_PROGRESS]: 'Der Status kann bis zum Abschluss der Bearbeitung beibehalten werden.',
    [ReportStatusType.COMPLETED]:
      'Nach erfolgreicher Fertigstellung der Meldung ist die Sichtbarkeit auf ein Jahr begrenzt.',
    [ReportStatusType.NOT_REALIZABLE]: 'Die geforderten Maßnahmen können nicht umgesetzt werden.',
    [ReportStatusType.EXTERNAL]: 'Die Meldung wurde an eine Organisation außerhalb des RadMELDERs weitergeleitet.',
    [ReportStatusType.ARCHIVED]: 'Die Meldung wird archiviert und auf Basisdaten reduziert',
  },

  UserStatus: {
    [UserStatus.ACTIVE]: 'Aktiv',
    [UserStatus.INACTIVE]: 'Inaktiv',
    [UserStatus.REGISTERED]: 'Registriert',
  },
  UserStatusDescription: {
    [UserStatus.ACTIVE]: 'Es besteht Zugriff auf die Plattform.',
    [UserStatus.INACTIVE]: 'Der Zugriff auf die Plattform ist gesperrt.',
    [UserStatus.REGISTERED]: 'Der/die Benutzer*in wartet auf Aktivierung.',
  },

  UserRole: {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.USER]: 'Benutzer*in',
  },
  UserRoleDescription: {
    [UserRole.ADMIN]:
      'Ermöglicht den Zugriff auf alle Meldungen, Verwaltungsebenen und Benutzer*innen auf der Plattform.',
    [UserRole.USER]:
      'Ermöglicht ausschließlich den Zugriff auf zugeordnete Verwaltungsebenen und deren zugehörigen Meldungen und Benutzer*innen.',
  },

  AdminLevelUserRole: {
    [AdminLevelUserRole.MANAGER]: 'Manager*in',
    [AdminLevelUserRole.EDITOR]: 'Bearbeiter*in',
    [AdminLevelUserRole.VIEWER]: 'Betrachter*in',
  },
  AdminLevelUserRoleDescription: {
    [AdminLevelUserRole.MANAGER]:
      'Ermöglicht die Bearbeitung der Verwaltungsebene sowie deren Meldungen und Benutzer*innen.',
    [AdminLevelUserRole.EDITOR]: 'Ermöglicht die Bearbeitung der Meldungen der Verwaltungsebene.',
    [AdminLevelUserRole.VIEWER]: 'Ermöglicht das Betrachten der Meldungen der Verwaltungsebene.',
  },

  AdminLevelUserStatus: {
    [AdminLevelUserStatus.ACTIVE]: 'Aktiv',
    [AdminLevelUserStatus.INACTIVE]: 'Inaktiv',
  },
  AdminLevelUserStatusDescription: {
    [AdminLevelUserStatus.ACTIVE]: 'Es besteht Zugriff auf die Verwaltungsebene.',
    [AdminLevelUserStatus.INACTIVE]: 'Der Zugriff auf die Verwaltungsebene ist gesperrt.',
  },

  UserNotificationInterval: {
    [UserNotificationInterval.IMMEDIATE]: 'Unmittelbar',
    [UserNotificationInterval.DAILY]: 'Täglich',
    [UserNotificationInterval.WEEKDAYS]: 'Werktags (Mo.-Fr.)',
    [UserNotificationInterval.WEEKLY]: 'Wöchentlich',
    [UserNotificationInterval.NEVER]: 'Keine',
  },

  DashboardTimePeriod: {
    [DashboardTimePeriod.THIS_WEEK]: 'Diese Woche',
    [DashboardTimePeriod.THIS_MONTH]: 'Dieses Monat',
    [DashboardTimePeriod.THIS_YEAR]: 'Dieses Jahr',
    [DashboardTimePeriod.LAST_7_DAYS]: 'Letzten 7 Tage',
    [DashboardTimePeriod.LAST_30_DAYS]: 'Letzten 30 Tage',
    [DashboardTimePeriod.LAST_90_DAYS]: 'Letzten 90 Tage',
    [DashboardTimePeriod.LAST_6_MONTHS]: 'Letzten 6 Moante',
    [DashboardTimePeriod.LAST_12_MONTHS]: 'Letzten 12 Monate',
    [DashboardTimePeriod.ALL]: 'Gesamter Zeitraum',
  },
};
